import React, { useRef, useState } from 'react';
import { ListFilter, X } from 'lucide-react';
import { Popover } from '@/components/ui/popover';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/tooltip';

/** Особый фильтр в строке поиска — чип «вид │ значение» (Алексей 17.09, AI17.7в/AI19):
 *  «разговор │ 21315», «проблема │ Нет товара», «тема │ Качество кофе». Цвет —
 *  под цвет того, на что нажали выше. */
export interface SearchChip {
  /** Вид фильтра; пусто — чип из одного значения («без ответа», AI27). */
  kind?: string;
  /** Средняя часть трёхчастного чипа: «апселл │ еда к напитку │ хот-дог» (#1404). */
  sub?: string;
  value: string;
  /** Цветная точка перед значением (источник отзывов). */
  dot?: string;
  /** Классы рамки/фона/текста чипа; по умолчанию — наш основной цвет. */
  tone?: string;
}

/** Двухчастная пилюля с разделительной линией: категория │ конкретика.
 *  Тем же видом рисуются теги в строках отзывов. */
export function SplitPill({ kind, sub, value, tone, dot, className, title, children }: {
  kind?: React.ReactNode;
  sub?: React.ReactNode;
  dot?: string;
  value: React.ReactNode;
  tone?: string;
  className?: string;
  title?: string;
  children?: React.ReactNode;
}) {
  return (
    <Tooltip content={title}><span
      className={cn(
        // Мягкая пилюля: короткий разделитель внутри, а не на всю высоту, —
        // сплошная черта делала её похожей на ячейку таблицы (AI27).
        'inline-flex h-6 min-w-0 max-w-full shrink-0 items-center gap-1.5 overflow-hidden whitespace-nowrap rounded-full border px-2.5 text-xs shadow-[0_1px_2px_rgb(0_0_0/0.04)]',
        tone ?? 'border-primary/30 bg-primary/5 text-primary',
        className,
      )}
    >
      {/* Тема жирным, конкретика обычным; высота фиксированная — длинное
          обрезается, полностью в подсказке (Алексей 17.09, AI23). */}
      {dot && <span aria-hidden className={cn('size-2 shrink-0 rounded-full', dot)} />}
      {kind != null && kind !== '' && (
        <>
          <span className="shrink-0 font-semibold">{kind}</span>
          <span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-25" />
        </>
      )}
      {sub != null && sub !== '' && (
        <>
          <span className="min-w-0 shrink truncate">{sub}</span>
          <span aria-hidden className="h-3 w-px shrink-0 bg-current opacity-25" />
        </>
      )}
      <span className={cn('min-w-0 truncate', (kind == null || kind === '') && 'font-medium')}>{value}</span>
      {children}
    </span></Tooltip>
  );
}

/** Строка поиска как во вкладке «Речь»: поле, «Найти», «сбросить» — плюс чип
 *  активного особого фильтра прямо в поле, снимается крестиком. */
export function SearchChipInput({
  draft, onDraftChange, onSubmit, onReset, chip, onClearChip, placeholder, submitLabel, resetLabel, active, className, extra,
}: {
  draft: string;
  onDraftChange: (v: string) => void;
  onSubmit: () => void;
  /** Сбрасывает и текст, и чип. */
  onReset: () => void;
  chip: SearchChip | null;
  onClearChip: () => void;
  placeholder: string;
  submitLabel: string;
  resetLabel: string;
  /** Есть ли что сбрасывать (применённый поиск или чип). */
  active: boolean;
  className?: string;
  /** Элемент справа внутри поля — например, кнопка-конструктор особого чипа (#1404). */
  extra?: React.ReactNode;
}) {
  return (
    <form
      className={cn('flex items-center gap-2', className)}
      onSubmit={(e) => { e.preventDefault(); onSubmit(); }}
    >
      {/* min-w-0: иначе природная ширина поля распирает форму на телефоне
          (см. вкладку «Речь», Safari). */}
      <div className="flex h-9 min-w-0 flex-1 items-center gap-1.5 rounded-md border bg-background pl-1.5 pr-1 focus-within:ring-2 focus-within:ring-ring">
        {chip && (
          <SplitPill kind={chip.kind} sub={chip.sub} value={chip.value} tone={chip.tone} dot={chip.dot} className="h-7 max-w-[70%] pr-1">
            <button
              type="button"
              onClick={onClearChip}
              aria-label={resetLabel}
              className="-mr-0.5 flex size-5 shrink-0 items-center justify-center rounded-full opacity-60 transition hover:bg-current/10 hover:opacity-100"
            >
              <X className="size-3" />
            </button>
          </SplitPill>
        )}
        <input
          type="search"
          value={draft}
          onChange={(e) => onDraftChange(e.target.value)}
          onKeyDown={(e) => {
            // Backspace в пустом поле снимает чип — как у тегов в почте.
            if (e.key === 'Backspace' && !draft && chip) onClearChip();
          }}
          placeholder={chip ? '' : placeholder}
          className="h-full min-w-0 flex-1 bg-transparent px-1.5 text-sm outline-none"
        />
        {extra}
      </div>
      <button
        type="submit"
        className="h-9 shrink-0 rounded-md bg-primary px-4 text-sm font-medium text-primary-foreground hover:bg-primary/90"
      >
        {submitLabel}
      </button>
      {active && (
        <button
          type="button"
          onClick={onReset}
          className="h-9 shrink-0 rounded-md px-2 text-xs text-muted-foreground hover:bg-muted"
        >
          {resetLabel}
        </button>
      )}
    </form>
  );
}

// ── Пошаговый конструктор особого чипа (AI253/AI255) ───────────────────────────
// Один компонент для речи и отзывов: что ищем → значение → (уточнение). Данные
// подаёт вкладка, предпросмотр — чип, который встанет в поиск.

export type PickerKind = { key: string; label: string; tone?: string };
export type PickerValue = { key: string; label: string; count?: number; any?: boolean; dot?: string };

export function ChipStepPicker({ title, kinds, valuesFor, optionsFor, textPlaceholder, numericKinds = [], preview, onPick, submitLabel = 'Найти' }: {
  title: string;
  kinds: PickerKind[];
  valuesFor: (kind: string) => PickerValue[];
  /** Частые уточнения выбранного значения; пусто — шага нет (кроме свободного текста, если задан плейсхолдер). */
  optionsFor?: (kind: string, value: string) => [string, number][];
  /** Плейсхолдер свободного текста уточнения; не задан — третьего шага нет. */
  textPlaceholder?: (kind: string) => string | null;
  /** Виды, где второй шаг — число (номер разговора). */
  numericKinds?: string[];
  preview: (kind: string, value: string, text: string) => SearchChip | null;
  onPick: (kind: string, value: string, text: string) => void;
  submitLabel?: string;
}) {
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [open, setOpen] = useState(false);
  const [kind, setKind] = useState('');
  const [value, setValue] = useState('');
  const [text, setText] = useState('');
  const numeric = numericKinds.includes(kind);
  const tone = kinds.find((k) => k.key === kind)?.tone;
  const values = kind && !numeric ? valuesFor(kind) : [];
  const options = kind && value && optionsFor ? optionsFor(kind, value) : [];
  const placeholder = kind && value && textPlaceholder ? textPlaceholder(kind) : null;
  const ready = numeric ? !!text.trim() : !!(kind && value);
  const chip = ready ? preview(kind, numeric ? text.trim() : value, numeric ? '' : text.trim()) : null;
  const pick = () => {
    if (!ready) return;
    onPick(kind, numeric ? text.trim() : value, numeric ? '' : text.trim());
    setOpen(false);
    setText('');
  };
  const reset = () => { setKind(''); setValue(''); setText(''); };
  const pillCls = (on: boolean, pillTone?: string) => cn(
    'inline-flex h-7 max-w-full items-center gap-1 rounded-full border px-2.5 text-xs transition',
    on ? (pillTone ?? 'border-primary/40 bg-primary/10 text-primary') + ' ring-1 ring-current/30 font-medium'
      : 'border-border bg-background text-foreground/80 hover:bg-muted',
  );
  const Step = ({ n, label, children }: { n: number; label: string; children: React.ReactNode }) => (
    <div className="space-y-1.5">
      <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-wide text-muted-foreground">
        <span className="flex size-4 items-center justify-center rounded-full bg-muted text-[10px] font-semibold text-foreground/70">{n}</span>
        {label}
      </div>
      {children}
    </div>
  );
  const inputCls = 'h-8 w-full rounded-md border border-border bg-background px-2 text-sm';
  return (
    <>
      <Tooltip content={title}><button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={title}
        aria-haspopup="dialog"
        aria-expanded={open}
        className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
      >
        <ListFilter className="size-4" />
      </button></Tooltip>
      <Popover open={open} onOpenChange={setOpen} triggerRef={triggerRef} side="bottom" align="end">
        <div className="w-[22rem] max-w-[92vw] space-y-3 p-3">
          <div className="flex items-center justify-between gap-2">
            <div className="text-sm font-medium">Особый фильтр</div>
            {kind && <button type="button" onClick={reset} className="text-[11px] text-muted-foreground hover:text-foreground">сначала</button>}
          </div>
          <div className="flex h-8 items-center rounded-md bg-muted/50 px-2">
            {chip ? (
              <SplitPill kind={chip.kind} sub={chip.sub} value={chip.value} tone={chip.tone} dot={chip.dot} className="h-6" />
            ) : (
              <span className="text-xs text-muted-foreground">{!kind ? 'Выбери, что искать' : numeric ? 'Введи номер' : 'Выбери значение'}</span>
            )}
          </div>
          <Step n={1} label="Что ищем">
            <div className="flex flex-wrap gap-1.5">
              {kinds.map((k) => (
                <button key={k.key} type="button" className={pillCls(kind === k.key, k.tone)} onClick={() => { setKind(k.key); setValue(''); setText(''); }}>
                  {k.label}
                </button>
              ))}
            </div>
          </Step>
          {numeric && (
            <Step n={2} label="Номер">
              <input className={inputCls} inputMode="numeric" placeholder="например, 21315" value={text} autoFocus
                onChange={(e) => setText(e.target.value.replace(/\D/g, ''))} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pick(); } }} />
            </Step>
          )}
          {kind && !numeric && (
            <Step n={2} label="Значение">
              {values.length ? (
                <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto">
                  {values.map((v) => (
                    <Tooltip key={v.key} content={v.any ? 'Любое значение с такой подписью' : undefined}><button type="button" className={pillCls(value === v.key, tone)}
                      onClick={() => { setValue(v.key); setText(''); }}>
                      {v.dot && <span aria-hidden className={cn('size-2 shrink-0 rounded-full', v.dot)} />}
                      <span className="truncate">{v.label}</span>
                      {v.count != null && <span className="opacity-60">{v.count}</span>}
                      {v.any && <span className="opacity-60">любое</span>}
                    </button></Tooltip>
                  ))}
                </div>
              ) : (
                <div className="text-xs text-muted-foreground">За период значений нет.</div>
              )}
            </Step>
          )}
          {kind && !numeric && value && (options.length > 0 || placeholder) && (
            <Step n={3} label="Уточнение · необязательно">
              {options.length > 0 && (
                <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto">
                  {options.slice(0, 12).map(([o, n]) => (
                    <button key={o} type="button" className={pillCls(text === o, tone)} onClick={() => setText(text === o ? '' : o)}>
                      <span className="truncate">{o}</span>
                      <span className="opacity-60">{n}</span>
                    </button>
                  ))}
                </div>
              )}
              {placeholder && (
                <input className={inputCls} placeholder={placeholder} value={text}
                  onChange={(e) => setText(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); pick(); } }} />
              )}
            </Step>
          )}
          <div className="flex items-center justify-end gap-2 pt-1">
            <button type="button" onClick={() => setOpen(false)} className="h-8 rounded-md px-3 text-xs text-muted-foreground hover:bg-muted">Отмена</button>
            <button type="button" disabled={!ready} onClick={pick}
              className="h-8 shrink-0 rounded-md bg-primary px-4 text-xs font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
              {submitLabel}
            </button>
          </div>
        </div>
      </Popover>
    </>
  );
}
